import React, { Suspense, useContext, useEffect, useState } from "react";
import PropertiesMap from "../../Components/PropertiesMap";
import { DataContext } from "../../Context/Context";
import axios from "axios";
import "./style.css";

import "swiper/css";
import "swiper/css/navigation";
import { differenceInDays, eachDayOfInterval, subDays } from "date-fns";
import { Link, useLocation } from "react-router-dom";
import DynamicSeoPage from "../DynamicSeoPage";
import { returnPriceWithTasxes } from "../../utilities/returnPriceWithTaxes";
import DynamicPriceCalculator from "../../utilities/DynamicPriceCalculator";

const formatPrice = (price) => {
  if (!price) return "N/A";
  return price.toLocaleString("en-AE", {
    style: "currency",
    currency: "AED",
    minimumFractionDigits: 0,
  });
};

const PropertiesOnMap = () => {
  const { startDate, endDate, setEndDate, setStartDate } =
    useContext(DataContext);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calendarData, setCalendarData] = useState({});
  const [visibleListings, setVisibleListings] = useState(10);

  const location = useLocation();

  const formatDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { selectedLocation, guestCounts } = location.state || {};

  useEffect(() => {
    const fetchListingsAndCalendar = async () => {
      setLoading(true);
      try {
        const apiUrl = "https://365luxuryhomes.com/api/listings";
        const params = {
          availabilityDateStart: formatDate(startDate),
          availabilityDateEnd: formatDate(endDate),
        };

        const listingsResponse = await axios.get(apiUrl, {
          params,
        });

        const listingsData = listingsResponse?.data || [];

        if (selectedLocation) {
          const filteredListings = listingsData?.filter((listing) => {
            const location = selectedLocation?.toLowerCase();
            const cityMatch = listing?.city?.toLowerCase().includes(location);
            const addressMatch = listing?.address
              ?.toLowerCase()
              .includes(location);
            return cityMatch || (!cityMatch && addressMatch);
          });
          setListings(filteredListings);
        } else {
          setListings(listingsData);
        }

        // Fetch calendar data for each listing
        const calendarPromises = listingsData.map(async (listing) => {
          const response = await axios.get(
            `https://365luxuryhomes.com/api/listing/calendar`,
            {
              params: {
                listingId: listing?.id,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
              },
            }
          );
          return { [listing?.id]: response.data };
        });

        const calendarResults = await Promise.all(calendarPromises);
        const calendarObj = Object.assign({}, ...calendarResults);
        setCalendarData(calendarObj);
      } catch (error) {
        console.error("Error fetching listings and calendar:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchListingsAndCalendar();
  }, [startDate, endDate, selectedLocation]);

  const calculateTotalPrice = (calendar, dateRange) => {
    if (!Array.isArray(calendar) || !dateRange) return 0;

    return dateRange.reduce((total, date) => {
      const day = calendar.find((item) => item.date === date);
      if (day && day.isAvailable) {
        return total + (day.price || 0);
      }
      return total;
    }, 0);
  };

  const getStartDatePrice = (calendar, startDate) => {
    if (!Array.isArray(calendar) || !startDate) return null;

    const day = calendar.find((item) => item.date === startDate);
    return day && day.isAvailable ? day.price : null;
  };

  const dateRange =
    startDate && endDate
      ? eachDayOfInterval({
          start: new Date(startDate),
          end: subDays(new Date(endDate), 1),
        }).map((date) => formatDate(date))
      : [];

  const handleChange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleLoadMore = () => {
    setVisibleListings((prev) => prev + 10);
  };

  const totalNights = differenceInDays(endDate, startDate);

  return (
    <section className="lg:pt-24 md:pt-24 pt-24 pb-10">
      <DynamicSeoPage
        page="listings"
        defalut={"Properties - 365 Luxury Homes"}
      />
      {loading ? (
        <div className="container">
          <div className="skeleton h-96 w-full flex items-center justify-center">
            <span className="loading loading-ring loading-lg"></span>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-8">
            <div className="container flex items-center justify-between ">
              <div className="flex items-center gap-1">
                <span className="lg:text-xl md:text-[18px] text-[16px] font-medium leading-none text-[#74716d]">
                  {listings?.length}
                </span>
                <span className="lg:text-xl md:text-[18px] text-[16px] font-medium leading-none text-[#74716d]">
                  Properties found
                </span>
              </div>
              <Link
                to={"/properties"}
                onClick={handleChange}
                className="px-6 py-2 text-[15px] bg-[#aca9a5] hover:bg-[#6c675b] text-white rounded-md hover:text-white h-[50px] flex items-center justify-center focus:text-white active:text-white hover:no-underline"
              >
                Close Map
              </Link>
            </div>
          </div>
          <div className="container px-[20px] flex lg:flex-row md:flex-col-reverse flex-col-reverse items-start gap-8">
            <div className="lg:w-[50%] md:w-full w-full flex flex-col gap-5">
              {listings.slice(0, visibleListings).map((listing) => (
                <Link
                  key={listing.id}
                  to={`/short-rental/${listing.slug}?statDate=${formatDate(
                    startDate
                  )}&endDate=${formatDate(endDate)}&guest=${
                    guestCounts?.guest || 0
                  }`}
                  className="flex lg:flex-row md:flex-row flex-col lg:gap-5 md:gap-4 gap-2 items-start hover:no-underline focus:no-underline focus:text-[#74716d] active:text-[#74716d]"
                >
                  <img
                    src={`https://365luxuryhomes.com/api/listings/image?url=${encodeURIComponent(
                      listing?.listingImages[0]?.url
                    )}&w=600&q=70&format=webp`}
                    alt=""
                    loading="lazy"
                    className="lg:w-[50%] md:w-[50%] w-full h-[240px] object-cover rounded-md"
                  />
                  <div className="lg:w-[50%] md:w-[50%] w-full">
                    <h2 className="text-[18px] leading-[24px] lg:mt-2 md:mt-2 mt-0 font-medium text-[#74716d]">
                      {listing?.name}
                    </h2>
                    <div className="flex items-center gap-4 my-2">
                      <div className="flex gap-1 items-center">
                        <span className="text-[15px] font-normal text-[#74716d]">
                          {listing?.personCapacity}
                        </span>
                        <span className="text-[15px] font-normal text-[#74716d]">
                          Guests
                        </span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <span className="text-[15px] font-normal text-[#74716d]">
                          {listing?.bedroomsNumber}
                        </span>
                        <span className="text-[15px] font-normal text-[#74716d]">
                          Bedrooms
                        </span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <span className="text-[15px] font-normal text-[#74716d]">
                          {listing?.bathroomsNumber}
                        </span>
                        <span className="text-[15px] font-normal text-[#74716d]">
                          Bathrooms
                        </span>
                      </div>
                    </div>
                    {startDate && endDate && (
                      <div>
                        <div className="text-[18px] font-medium text-[#74716d] my-2">
                          {formatPrice(
                            getStartDatePrice(
                              calendarData[listing.id],
                              formatDate(startDate)
                            ) * 1.05
                          ) || "N/A"}
                          /night
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {listing?.listingAmenities?.slice(0, 5).map((amenity) => (
                        <span
                          key={amenity?.id}
                          className="px-[6px] py-[3px] border border-[#aca9a5] text-[#74716d] text-[14px] rounded-md"
                        >
                          {amenity?.amenityName}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
              {visibleListings < listings.length && (
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-2 mt-4 text-[15px] bg-[#aca9a5] hover:bg-[#6c675b] text-white rounded-[4px] hover:text-white focus:text-white active:text-white"
                >
                  Load More
                </button>
              )}
            </div>
            <div className="lg:w-[50%] md:w-full w-full sticky-section rounded-md overflow-hidden">
              <Suspense
                fallback={<div className="skeleton h-full w-full"></div>}
              >
                <PropertiesMap listings={listings} />
              </Suspense>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default PropertiesOnMap;
